import { File as FileIcon, FileText, Image as ImageIcon, Upload, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const formatFileSize = (bytes: number) => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (file: File) => {
	if (file.type.startsWith("image/")) return ImageIcon;
	if (file.type === "application/pdf") return FileText;
	return FileIcon;
};

interface CanvasUploadModalProps {
	open: boolean;
	onClose: () => void;
}

/**
 * File upload modal opened from the canvas action bar. Keeps its file list
 * while closed (the component stays mounted; only the dialog unmounts).
 */
export function CanvasUploadModal({ open, onClose }: CanvasUploadModalProps) {
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [isDragOver, setIsDragOver] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			setUploadedFiles((prev) => [...prev, ...files]);
		}
	}, []);

	const handleFileSelect = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || []);
			if (files.length > 0) {
				setUploadedFiles((prev) => [...prev, ...files]);
			}
			e.target.value = "";
		},
		[],
	);

	const removeFile = useCallback((index: number) => {
		setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
	}, []);

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="fixed inset-0 z-50 flex items-center justify-center"
					onClick={onClose}
				>
					{/* Backdrop */}
					<div className="absolute inset-0 bg-scrim/30 backdrop-blur-sm" />

					{/* Modal */}
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 10 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: 10 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="relative w-full max-w-lg mx-4 rounded-2xl border border-border-subtle bg-bg-surface backdrop-blur-xl shadow-popover overflow-hidden"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
							<h3 className="text-lg font-semibold text-text-primary">
								Upload Files
							</h3>
							<Button variant="ghost" size="icon-sm" onClick={onClose}>
								<X size={18} />
							</Button>
						</div>

						{/* Drop Zone */}
						<div className="px-6 py-5">
							<div
								onDragOver={(e) => {
									e.preventDefault();
									setIsDragOver(true);
								}}
								onDragLeave={() => setIsDragOver(false)}
								onDrop={handleFileDrop}
								onClick={() => fileInputRef.current?.click()}
								className={cn(
									"relative flex flex-col items-center justify-center gap-3 px-6 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200",
									isDragOver
										? "border-brand-700 bg-brand-50"
										: "border-border-subtle hover:border-border-control hover:bg-bg-sunken/50",
								)}
							>
								<div
									className={cn(
										"p-3 rounded-full transition-colors",
										isDragOver ? "bg-brand-100" : "bg-bg-canvas",
									)}
								>
									<Upload
										size={24}
										className={cn(
											"transition-colors",
											isDragOver ? "text-text-primary" : "text-text-tertiary",
										)}
									/>
								</div>
								<div className="text-center">
									<p className="text-sm font-medium text-text-secondary">
										{isDragOver
											? "Drop files here"
											: "Click or drag files to upload"}
									</p>
									<p className="text-xs text-text-tertiary mt-1">
										PDF, DOCX, PPTX, XLS, JPG, PNG, MP4
									</p>
								</div>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept=".pdf,.docx,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4,.svg"
									onChange={handleFileSelect}
									className="hidden"
								/>
							</div>

							{/* File List */}
							{uploadedFiles.length > 0 && (
								<div className="mt-4 space-y-2 max-h-[200px] overflow-y-auto scrollbar-thin">
									{uploadedFiles.map((file, i) => {
										const Icon = getFileIcon(file);
										return (
											<motion.div
												key={`${file.name}-${i}`}
												initial={{ opacity: 0, x: -10 }}
												animate={{ opacity: 1, x: 0 }}
												exit={{ opacity: 0, x: 10 }}
												transition={{ duration: 0.15, delay: i * 0.03 }}
												className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-bg-canvas border border-border-subtle group"
											>
												<div className="p-1.5 rounded-lg bg-bg-canvas">
													<Icon size={16} className="text-text-tertiary" />
												</div>
												<div className="flex-1 min-w-0">
													<p className="text-xs font-medium text-text-secondary truncate">
														{file.name}
													</p>
													<p className="text-2xs text-text-tertiary">
														{formatFileSize(file.size)}
													</p>
												</div>
												<Button
													variant="ghost"
													size="icon-xs"
													className="opacity-0 group-hover:opacity-100 transition-all hover:text-danger hover:bg-danger-bg"
													onClick={() => removeFile(i)}
												>
													<X size={14} />
												</Button>
											</motion.div>
										);
									})}
								</div>
							)}
						</div>

						{/* Footer */}
						<div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle">
							<p className="text-xs text-text-tertiary">
								{uploadedFiles.length > 0
									? `${uploadedFiles.length} file${uploadedFiles.length > 1 ? "s" : ""} selected`
									: "No files selected"}
							</p>
							<div className="flex items-center gap-2">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										setUploadedFiles([]);
										onClose();
									}}
								>
									Cancel
								</Button>
								<Button
									size="sm"
									disabled={uploadedFiles.length === 0}
									onClick={() => {
										toast.success(
											`${uploadedFiles.length} file${uploadedFiles.length > 1 ? "s" : ""} uploaded`,
										);
										setUploadedFiles([]);
										onClose();
									}}
								>
									Upload
								</Button>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
